/**
 * The chest in 21 Wild's colours. The chest itself is Blockari's, ported
 * exactly (geometry, camera, lights, timeline): what changes is this table,
 * the materials the mesh is shaded in (felt for planks, a spade for the
 * jewel, chips for coins — chest-mesh.ts), and the confetti.
 *
 * The ladder is the family's — slate, cyan, violet, gold — and the grounds
 * are its deep tones, the way Blockari's grounds are its jewels' deep tones.
 * Mirrored as `--w21-rarity-*` in ui.css for the titles and the pill.
 */

export interface RarityMaterial {
  /** Body (felt) and its darker foot / side tone. */
  body: number;
  bodyDark: number;
  /** Bands, rim, lock plate, the clasp: metal. */
  metal: number;
  metalDark: number;
  /** The clasp's emblem (the spade's enamel). */
  gem: number;
  /** Rim light and the glow ring under the chest. */
  glow: number;
  /** The screen's ground: centre and edge of the radial gradient. */
  groundInner: number;
  groundOuter: number;
  /** The title's colour. */
  title: number;
}

export const RARITY_MATERIALS: Readonly<Record<'common' | 'rare' | 'epic' | 'legendary', RarityMaterial>> = {
  common: {
    body: 0x94a3b8,
    bodyDark: 0x4b5670,
    metal: 0xffc53d,
    metalDark: 0xc98a12,
    gem: 0x0a0710,
    glow: 0xb8c6dc,
    groundInner: 0x2c3548,
    groundOuter: 0x0a0c14,
    title: 0xcbd5e1,
  },
  rare: {
    body: 0x22d3ee,
    bodyDark: 0x0e7490,
    metal: 0xffc53d,
    metalDark: 0xc98a12,
    gem: 0x0a0710,
    glow: 0x5ee6f7,
    groundInner: 0x0b4a5c,
    groundOuter: 0x03131a,
    title: 0x22d3ee,
  },
  epic: {
    body: 0xa855f7,
    bodyDark: 0x5b21b6,
    metal: 0xffc53d,
    metalDark: 0xc98a12,
    gem: 0x0a0710,
    glow: 0xc084fc,
    groundInner: 0x40166f,
    groundOuter: 0x10051f,
    title: 0xc084fc,
  },
  legendary: {
    body: 0xa8203f,
    bodyDark: 0x5c0d22,
    // A deep casino red (burgundy felt) under bright yellow gold: the gold glow and rays stay, and the hardware reads
    // against them (it was gold on gold, so the body, bands, lid and lock lost each other).
    metal: 0xffd24a,
    metalDark: 0xb8760c,
    gem: 0x0a0710,
    glow: 0xffd166,
    groundInner: 0x7a4a08,
    groundOuter: 0x1c0f02,
    title: 0xffc53d,
  },
};

/** The open's confetti: the table's colours (tokens.palette), and white. */
export const CONFETTI_COLOURS: readonly number[] = [
  0xffc53d, // gold
  0x34d399, // mint
  0xf43f5e, // rose
  0x4cc9f0, // azure light
  0xd873ff, // violet light
  0xfb923c, // orange
  0xa3e635, // lime
];

/** The pedestal's drum, in the table's plum felt (Blockari's is navy stone). */
export const PEDESTAL = {
  side: 0x1b1030,
  foot: 0x120a20,
  top: 0x2e1d52,
  inner: 0x24163f,
  chips: 0x2a1a4a,
} as const;
